import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Typography, InputAdornment, MenuItem, CircularProgress } from '@mui/material';
import {
  FaIdCard, FaUniversity, FaCalendarAlt, FaCalendarCheck, FaBackward, FaBookOpen,
  FaMapMarkedAlt, FaCity, FaGlobeAsia, FaGlobe, FaEnvelope, FaFileUpload, FaTimes
} from 'react-icons/fa';
import PopupModel from '../../popup-model/PopupModel';
import IndianStatesSelect from '../ListOfStates/IndianStates';
import { saveHigherSecondaryData, getHigherSecondaryData, deleteEducation} from '../../../services/candidate/api';
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from 'react-toastify';
const indianQualifications = [
  'Higher Secondary',
  'HSC',
  'CBSE',
  'ICSE',
  'ISC',
  '10+2',
  'Senior Secondary',
  'Intermediate',
  'Pre-University',
  'A-Level',
];

const StepHigherSecondary = ({ onNext, onBack, defaultData = {} }) => {
  const [form, setForm] = useState({
    enrollmentNumber: defaultData.enrollmentNumber || '',
    instituteName: defaultData.instituteName || '',
    qualification: defaultData.qualification || 'Higher Secondary',
    specialization: defaultData.specialization || '',
    enrolledYear: defaultData.enrolledYear || '',
    passingYear: defaultData.passingYear || '',
    address: defaultData.address || '',
    city: defaultData.city || '',
    stateRegionProvince: defaultData.stateRegionProvince || '',
    country: defaultData.country || '',
    zipCode: defaultData.zipCode || '',
  });

  const [certificate, setCertificate] = useState(null); // null, File, or {url, name}
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const certificateFileRef = useRef(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [error, setError] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [enabledDelete, setEnabledDelete] = useState(false); 

  // Helper function to extract file name from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop());
    } catch {
      return url.split('/').pop() || 'certificate.pdf';
    }
  };

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getHigherSecondaryData();


        if (data && Object.keys(data).length > 0) {
          const formatDate = (dateStr) => {
            if (!dateStr) return '';
            if (/^(\d{4}|\d{2}-\d{2}-\d{4}|\d{2}-\d{4})$/.test(dateStr)) {
              return dateStr;
            }
            return '';
          };

          setForm({
            enrollmentNumber: data.enrollmentNumber || '',
            instituteName: data.instituteName || '',
            qualification: data.qualification || 'Higher Secondary',
            specialization: data.specialization || '',
            enrolledYear: formatDate(data.enrolledYear),
            passingYear: formatDate(data.passingYear),
            address: data.address || '',
            city: data.city || '',
            stateRegionProvince: data.stateRegionProvince || '',
            country: data.country || '',
            zipCode: data.zipCode || '',
          });
          setCertificate(data.certificateFileUrl ? { url: data.certificateFileUrl, name: getFileNameFromUrl(data.certificateFileUrl) } : null);
          setEducationLevel(data.educationLevel);
          setEnabledDelete(true);
        } else {

          setForm({
            enrollmentNumber: '',
            instituteName: '',
            qualification: 'Higher Secondary',
            specialization: '',
            enrolledYear: '',
            passingYear: '',
            address: '',
            city: '',
            stateRegionProvince: '',
            country: '',
            zipCode: '',
          });
          setCertificate(null);
          setValidationErrors({});
        }
      } catch (err) {

        // Initialize empty form without showing popup
        setForm({
          enrollmentNumber: '',
          instituteName: '',
          qualification: 'Higher Secondary',
          specialization: '',
          enrolledYear: '',
          passingYear: '',
          address: '',
          city: '',
          stateRegionProvince: '',
          country: '',
          zipCode: '',
        });
        setCertificate(null);
        setValidationErrors({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to check if the form is empty
  const isFormEmpty = () => {
    const isEmpty = (
      !form.enrollmentNumber &&
      !form.instituteName &&
      form.qualification === 'Higher Secondary' &&
      !form.specialization &&
      !form.enrolledYear &&
      !form.passingYear &&
      !form.address &&
      !form.city &&
      !form.stateRegionProvince &&
      !form.country &&
      !form.zipCode &&
      !certificate
    );
    return isEmpty;
  };

      const confirmDelete = async() => {
    
         try {
            setLoadingProfile
            (true);
            const response = await deleteEducation(educationLevel);
        
            toast.success("Higher secondary details deleted successfully", {
                autoClose: 3000,
            });
           // Reset form to empty state
      setForm({
      enrollmentNumber: '',
      instituteName: '',
      qualification: '',
      enrolledYear: '',
      passingYear: '',
      address: '',
      city: '',
      stateRegionProvince: '',
      country: '',
      zipCode: '',
    });
    
    // Reset other states
    setCertificate(null);
    setEducationLevel(null);
    setEnabledDelete(false);
    setValidationErrors({});
    setShowPopup(false);
  
    
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                err.message ||
                'Failed to delete higher secondary details.';
            
            toast.error(errorMessage, {
                autoClose: 3000,
            });
    
            setError(errorMessage);
        } finally {
            setLoadingProfile(false);
        }
      };
  
  const getDateValue = (dateStr) => {
    if (!dateStr || !dateStr.includes('-')) return 0;
    const [month, year] = dateStr.split('-').map(Number);
    return year * 12 + month;
  };

  // Validation logic
  const validateField = (name, value) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const datePattern = /^(\d{2})-(\d{4})$/;
    let error = '';

    switch (name) {
      case 'enrollmentNumber':
        if (!value) {
          return 'EnrollmentNumber is required';
        }
        if (value && !/^[a-zA-Z0-9 ]{1,20}$/.test(value)) {
          error = 'Enrollment number must be alphanumeric and up to 20 characters';
        }
        break;
       case 'instituteName':
        if (!value) error = 'InstituteName is required';
        else if (!/^[A-Za-z .'-]{1,50}$/.test(value)) 
        error = 'Institute name must contain only alphabets, spaces, dots, or hyphens (1-50 characters)';
        break;
      case 'qualification':
        if (!value) {
          return 'Qualification is required';
        }
        if (!indianQualifications.includes(value)) {
          error = 'Please select a valid qualification from the list';
        }
        break;
      case 'specialization':
        if (!value) {
          return 'Specialization is required';
        }
        if (value && !/^[A-Za-z ]{1,50}$/.test(value)) {
          error = 'Specialization must contain only alphabets and spaces (up to 50 characters)';
        }
        break;
      case 'enrolledYear':
      case 'passingYear':
        if (!value) {
          error = `${name === 'enrolledYear' ? 'Enrolled' : 'Passing'} year is required`;
        } else if (!datePattern.test(value)) {
          error = 'Must be in MM-YYYY format';
        } else {
          const [month, year] = value.split('-').map(Number);
          if (month < 1 || month > 12) {
            error = 'Month must be between 01 and 12';
          } else if (year < 1900) {
            error = 'Year must be after 1900';
          } else if (year > currentYear || (year === currentYear && month > currentMonth)) {
            error = 'Cannot be in the future';
          }
        }
        break;
      case 'city':
        if (!value) {
          return 'City is required';
        }
        if (value && !/^[A-Za-z ]{1,25}$/.test(value)) {
          error = 'City must contain only alphabets and spaces (1-25 characters)';
        }
        break;
      case 'stateRegionProvince':
        if (!value) {
          return 'State is required';
        }
        if (value && !/^[A-Za-z ]{1,20}$/.test(value)) {
          error = 'State must contain only alphabets and spaces (1-20 characters)';
        }
        break;
      case 'country':
        if (!value) {
          return 'Country is required';
        }
        if (value && !/^[A-Za-z ]{1,30}$/.test(value)) {
          error = 'Country must contain only alphabets and spaces (1-30 characters)';
        }
        break;
      case 'zipCode':
        if (value && !/^\d{6}$/.test(value)) {
          error = 'Zip code must be exactly 6 digits';
        }
        break;
      case 'certificate':
        if (!value) {
          error = 'Certificate file is required';
        } else if (value instanceof File) {
          if (value.size > 5 * 1024 * 1024) {
            error = 'Certificate file must be less than 5MB';
          } else if (!['image/jpeg', 'image/png', 'application/pdf'].includes(value.type)) {
            error = 'Certificate file must be PDF or image (JPEG/PNG)';
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateAndSetError = (name, value) => {
    const error = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };
  // Combined handleChange and handleBlur logic
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "enrollmentNumber") {
      updatedValue = value.replace(/[^A-Za-z0-9 ]/g, "").slice(0, 20);
    }
    if (name === "address") {
      updatedValue = value.replace(/[^A-Za-z0-9-/ ]/g, "").slice(0, 30);
    }
    if (name === "zipCode") {
      updatedValue = value.replace(/\D/g, "").slice(0, 6); 
    }
    if (name === "instituteName") {
      updatedValue = value.replace(/[^A-Za-z .'-]/g, "").slice(0, 50);
    }
    if (name === "city") {
      updatedValue = value.replace(/[^A-Za-z ]/g, "").slice(0, 25);
    }
    if (name === "country") {
      updatedValue = value.replace(/[^A-Za-z ]/g, "").slice(0, 30);
    }
    if (name === "specialization") {
      updatedValue = value.replace(/[^A-Za-z ]/g, "").slice(0, 50);
    }
    setForm((prev) => ({ ...prev, [name]: updatedValue }));
    validateAndSetError(name, updatedValue);
    if (name === 'enrolledYear' || name === 'passingYear') {
      const other = name === 'enrolledYear' ? 'passingYear' : 'enrolledYear';
      validateAndSetError(other, form[other]);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  // File upload handler
  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      const error = validateField('certificate', file); // Pass the file explicitly to validate
      setValidationErrors((prevErrors) => ({ ...prevErrors, certificate: error }));
    } else {

    }
  };
      const cancelDelete = () => {
    setShowPopup(false);
  };
 const handleDelete = () => {
    setShowPopup(true);
  };

  // Remove file
  const handleRemoveCertificate = () => {
    setCertificate(null);
    setValidationErrors((prevErrors) => ({ ...prevErrors, certificate: validateField('certificate', null) }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    Object.keys(form).forEach((key) => {
      if (key !== 'certificateFile') {
        const error = validateField(key, form[key]);
        if (error) errors[key] = error;
      }
    });
    const certError = validateField('certificate', certificate);
    if (certError) errors.certificate = certError;
    if (form.enrolledYear || form.passingYear) {
      const enrollVal = getDateValue(form.enrolledYear);
      const passVal = getDateValue(form.passingYear);
      if (enrollVal && passVal && enrollVal >= passVal) {
        errors.enrolledYear = 'Enrolled date must be before passing date';
        errors.passingYear = 'Passing date must be after enrolled date';
      }
    }
    return errors;
  };

  // Submission handler
  const handleSubmit = async () => {

    if (isFormEmpty()) {

      onNext({ skipped: true });
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {

      setValidationErrors(errors);
      setSubmissionResult({
        isSuccess: false,
        title: 'Validation Error',
        message: 'Please fix the errors in the form.',
      });
      setShowResultPopup(true);
      return;
    }
    setLoading(true);
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== 'certificateFile' && value) payload.append(key, value);
    });

    try {
      if (certificate instanceof File) {
        payload.append('certificateFile', certificate);
      }
      for (let [key, value] of payload.entries()) {

      }

      await saveHigherSecondaryData(payload);

      setSubmissionResult({
        isSuccess: true,
        title: 'Success',
        message: 'Higher Secondary data saved successfully!',
      });
      setShowResultPopup(true);
      onNext(form);
    } catch (err) {
      let errorMessage = err.response?.data?.message || err.message || 'Failed to save higher secondary data';
      if (err.response?.status === 400) {
        errorMessage = 'Invalid data submitted. Please check your inputs and try again.';
        if (err.response?.data?.errors) {
          const backendErrors = {};
          err.response.data.errors.forEach((curr) => {
            backendErrors[curr.field] = curr.defaultMessage;
          });
          setValidationErrors(backendErrors);
        }
      } else if (err.response?.status === 401) {
        errorMessage = 'Unauthorized request. Please ensure you are logged in.';
      } else if (err.response?.status === 503) {
        errorMessage = 'The server is temporarily unavailable. Please try again later.';
      }
      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: errorMessage,
      });
      setShowResultPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };

  const triggerCertificateUpload = () => {
    certificateFileRef.current.click();
  };

  return (
    <Box className="stepHighSchoolContainer">
      {loading && (
        <Box className="loading-overlay">
          <Box className="loading-content">
            <CircularProgress size={40} style={{ color: '#4D0054' }} />
            <Typography className="loading-text">{loading ? 'Saving...' : 'Loading...'}</Typography>
          </Box>
        </Box>
      )}
      <div className="highschool-header">
        <div className="vertical-line"></div>
        <Typography className="stepHighSchoolTitle">🎓 Higher Secondary Details</Typography>
      </div>

      <Box className="stepHighSchoolGrid">
        <TextField
          label="Enrollment Number / Hall Ticket"
          name="enrollmentNumber"
          value={form.enrollmentNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.enrollmentNumber}
          helperText={validationErrors.enrollmentNumber}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaIdCard color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Institute Name"
          name="instituteName"
          value={form.instituteName}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.instituteName}
          helperText={validationErrors.instituteName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Qualification"
          name="qualification"
          value={form.qualification}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          select
          error={!!validationErrors.qualification}
          helperText={validationErrors.qualification}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        >
          {indianQualifications.map((qual) => (
            <MenuItem key={qual} value={qual}>
              {qual}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Specialization"
          name="specialization"
          value={form.specialization}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.specialization}
          helperText={validationErrors.specialization}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaBookOpen color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />
<LocalizationProvider dateAdapter={AdapterDayjs}>
  
  {/* Enrolled Year */}
  <DatePicker
  label="Enrolled Year"
  views={["year", "month"]}
  format="MM-YYYY"
  disableFuture
  value={form.enrolledYear ? dayjs(form.enrolledYear, "MM-YYYY") : null}
  onChange={(newValue) => {
    const formatted = newValue ? newValue.format("MM-YYYY") : "";

    setForm((prev) => ({
      ...prev,
      enrolledYear: formatted,
    }));

    validateAndSetError("enrolledYear", formatted);
  }}
  slotProps={{
    textField: {
      name: "enrolledYear",
      variant: "standard",
      fullWidth: true,
      onBlur: handleBlur,
      error: !!validationErrors.enrolledYear,
      helperText: validationErrors.enrolledYear,
      InputLabelProps: { shrink: true },
      InputProps: {
        startAdornment: (
          <InputAdornment position="start">
            <FaCalendarAlt color="#4D0054" />
          </InputAdornment>
        ),
      },
    },
  }}
/>

  {/* Passing Year */}
 <DatePicker
   label="Passing Year"
   views={["year", "month"]}
   format="MM-YYYY"
   disableFuture
   value={form.passingYear ? dayjs(form.passingYear, "MM-YYYY") : null}
   onChange={(newValue) => {
     const formatted = newValue ? newValue.format("MM-YYYY") : "";
 
     setForm((prev) => ({
       ...prev,
       passingYear: formatted,
     }));
 
     validateAndSetError("passingYear", formatted);
   }}
   slotProps={{
     textField: {
       name: "passingYear",
       variant: "standard",
       fullWidth: true,
       onBlur: handleBlur,
       error: !!validationErrors.passingYear,
       helperText: validationErrors.passingYear,
       InputLabelProps: { shrink: true },
       InputProps: {
         startAdornment: (
           <InputAdornment position="start">
             <FaCalendarAlt color="#4D0054" />
           </InputAdornment>
         ),
       },
     },
   }}
 />

</LocalizationProvider>

        <TextField
          label="Address (optional)"
          name="address"
          value={form.address}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField stepHighSchoolFull"
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaMapMarkedAlt color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="City"
          name="city"
          value={form.city}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.city}
          helperText={validationErrors.city}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaCity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />


        <IndianStatesSelect
          value={form.stateRegionProvince}
          onChange={handleChange}
          onBlur={handleBlur}
          error={validationErrors.stateRegionProvince}
          helperText={validationErrors.stateRegionProvince}
          className="stepHighSchoolField"
        />

        <TextField
          label="Country"
          name="country"
          value={form.country}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.country}
          helperText={validationErrors.country}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaGlobe color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Zip Code (optional)"
          name="zipCode"
          value={form.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.zipCode}
          helperText={validationErrors.zipCode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaEnvelope color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <Box className="upload-container">
          <p className="upload-label-title"><FaFileUpload className="filed-icons" />Higher Secondary Certificate*</p>
          <div className="upload-certificate-group">
            <input
              type="file"
              id="certificateUpload"
              style={{ display: "none" }}
              ref={certificateFileRef}
              onChange={handleCertificateUpload}
              hidden
              accept="image/jpeg,image/png,application/pdf"
            />
            <div onClick={triggerCertificateUpload} className="upload-certificate-button">
              Drag & Drop or Click to Upload
            </div>
            {certificate && (
              <div className="file-list">
                <span className="file-list-item">
                  {certificate instanceof File ? certificate.name : certificate.name || 'Existing File'}
                  <FaTimes
                    className="remove-file-icon"
                    onClick={handleRemoveCertificate}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleRemoveCertificate();
                      }
                    }}
                    aria-label={`Remove ${certificate instanceof File ? certificate.name : certificate.name || 'Existing File'}`}
                  />
                </span>
              </div>
            )}
            {validationErrors.certificate && (
              <Typography color="error" variant="caption">
                {validationErrors.certificate}
              </Typography>
            )}
            <p className="upload-certificate-note">* File must be less than 5MB and must be PDF.</p>
          </div>
        </Box>
      </Box>

      <PopupModel
        show={showResultPopup}
        onClose={closeResultPopup}
        title={submissionResult?.title}
        message={submissionResult?.message}
        isSuccess={submissionResult?.isSuccess}
      />

      <Box className="stepHighSchoolButtonGroup">
        <div>
          {enabledDelete &&  (
            <button onClick={handleDelete} className="stepHighSchooldelButton" disabled={loading}>
              Delete
            </button>
             )}
        </div>
       
        <div className="stepHighSchoolrightButtonGroup">
        <div className="button-left">
          <button
            onClick={() => {

              onBack();
            }}
            className="stepHighSchoolButton secondary"
          >
            <FaBackward style={{ marginRight: '6px' }} /> Previous
          </button>
        </div>

        <div className="button-right">
          <button onClick={handleSubmit} className="stepHighSchoolButton" disabled={loading}>
            {loading ? 'Saving...' : 'Next'}
          </button>
        </div>
            {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h2 className="popup-title">Confirmation</h2>
            <p>Are you sure you want to delete this higher secondary record?</p>
            <div className="candidate-education">
            
              <button onClick={cancelDelete} className="popup-close-btn error">
                Cancel
              </button>
                <button onClick={confirmDelete} className="popup-close-btn success ">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </Box>
    </Box>
  );
};

export default StepHigherSecondary;